/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/

/**
 * useImageDialog.js
 * @description Custom React hook for managing image dialog state.
 * Handles the visibility of the image viewing dialog and the current image index.
 * Provides methods to open/close the dialog and navigate between images.
 *
 * @hook
 * @example
 * const { isOpen, activeImageIndex, onOpen, onClose, onChangeIndex } = useImageDialog()
 */
import { useState } from 'react'

/**
 * Custom hook to manage image dialog state and handlers
 * @returns {Object} Image dialog state and handlers
 * @returns {boolean} .isOpen - Whether the image dialog is currently open
 * @returns {number} .activeImageIndex - Index of the currently displayed image
 * @returns {Function} .onOpen - Function to open the image dialog
 * @returns {Function} .onClose - Function to close the image dialog and reset state
 * @returns {Function} .onChangeIndex - Function to change the active image index
 */
export const useImageDialog = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeImageIndex, setActiveImageIndex] = useState(0)

  const onOpen = () => {
    setIsOpen(true)
  }

  const onClose = () => {
    setIsOpen(false)
    setActiveImageIndex(0)
  }

  const onChangeIndex = (index) => {
    setActiveImageIndex(index)
  }

  return {
    isOpen,
    activeImageIndex,
    onOpen,
    onClose,
    onChangeIndex,
  }
}
